// Tutorial.tsx — v030: mini tutorial della partita (1 contro 1, torneo, partita a 4). Un disegno della schermata della
// partita con le icone vere al loro posto; a ogni passo un pulsante si illumina e sotto c'è la spiegazione. Si apre da
// solo la prima volta che si entra in quel tipo di partita (prima di «Sono pronto») e dalla Home («Tutorial»).
import { vibra } from "../servizi/nativo.ts";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { IconaEsci, IconaRisata, IconaSegnala, IconaSuoni } from "./IconeGioco.tsx";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { PREFERENZE } from "../costanti.ts";
import { vistiCambiati } from "../servizi/account.ts";

export type TipoTutorial = "1v1" | "torneo" | "gruppo";
type Punto = "video" | "risata" | "filtro" | "voce" | "segnala" | "esci" | "suoni" | "torneo";

const PASSI: Record<TipoTutorial, Punto[]> = {
  "1v1": ["video", "risata", "filtro", "voce", "segnala", "esci"],
  torneo: ["torneo", "video", "risata", "filtro", "voce", "segnala", "esci"],
  gruppo: ["video", "risata", "filtro", "voce", "segnala", "esci"],
};

/** Già visto su questo telefono? */
export function tutorialVisto(tipo: TipoTutorial): boolean {
  try {
    return JSON.parse(leggiPreferenza(PREFERENZE.tutorial) ?? "{}")[tipo] === true;
  } catch {
    return false;
  }
}
function segnaVisto(tipo: TipoTutorial): void {
  let v: Record<string, boolean> = {};
  try {
    v = JSON.parse(leggiPreferenza(PREFERENZE.tutorial) ?? "{}") as Record<string, boolean>;
  } catch {
    v = {};
  }
  salvaPreferenza(PREFERENZE.tutorial, JSON.stringify({ ...v, [tipo]: true }));
  vistiCambiati(); // v041: anche sull'account
}

function IconaFiltro() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0M9 9.5h.01M15 9.5h.01" />
    </svg>
  );
}
function IconaVoce() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
    </svg>
  );
}

/** Un pulsante del disegno: si illumina quando è il suo passo. */
function Segno({ punto, attivo, classe, children }: { punto: Punto; attivo: Punto; classe: string; children: ReactNode }) {
  return (
    <span className={`ld-tut-segno ${classe}`} data-punto={punto} data-attivo={attivo === punto}>
      {children}
    </span>
  );
}

function Disegno({ tipo, attivo }: { tipo: TipoTutorial; attivo: Punto }) {
  const { t } = useTranslation();
  const altri = tipo === "gruppo" ? ["Bea", "Carlo", "Dani"] : [tipo === "torneo" ? "Luca" : "Anna"];
  const riquadroAltro = (nome: string, i: number) => (
    <div key={nome} className="ld-tut-riquadro" data-acceso={attivo === "video"}>
      <span className="ld-tut-faccia" aria-hidden="true">{["😐", "🙂", "😶"][i]}</span>
      {i === 0 && (
        <>
          <Segno punto="segnala" attivo={attivo} classe="ld-tut-alto-destra ld-tut-rosso">
            <IconaSegnala />
          </Segno>
          <Segno punto="risata" attivo={attivo} classe="ld-tut-basso-sinistra ld-tut-giallo">
            <IconaRisata />
          </Segno>
        </>
      )}
      <span className="ld-tut-nome">{nome}</span>
    </div>
  );
  return (
    <div className={`ld-tut-telefono ld-tut-${tipo}`} aria-hidden="true">
      <div className="ld-tut-testa">
        <Segno punto="esci" attivo={attivo} classe="">
          <IconaEsci />
        </Segno>
        {tipo === "torneo" ? (
          <span className="ld-tut-chip" data-acceso={attivo === "torneo"}>
            {t("tutorial.semifinale")}
          </span>
        ) : (
          <span className="ld-tut-chip">0:12</span>
        )}
        <Segno punto="suoni" attivo={attivo === "esci" ? "suoni" : attivo} classe="">
          <IconaSuoni acceso />
        </Segno>
      </div>
      <div className={tipo === "gruppo" ? "ld-tut-griglia" : "ld-tut-colonna"}>
        {altri.map(riquadroAltro)}
        <div className="ld-tut-riquadro ld-tut-io" data-acceso={attivo === "video"}>
          <span className="ld-tut-faccia" aria-hidden="true">
            😏
          </span>
          <Segno punto="filtro" attivo={attivo} classe="ld-tut-alto-destra">
            <IconaFiltro />
          </Segno>
          <Segno punto="voce" attivo={attivo} classe="ld-tut-sotto-filtro">
            <IconaVoce />
          </Segno>
          <span className="ld-tut-nome">{t("gioco.tu")}</span>
        </div>
      </div>
    </div>
  );
}

interface Props {
  tipo: TipoTutorial;
  /** chiuso (fine, salta o ✕): il tutorial risulta visto */
  onFine: () => void;
}

export function Tutorial({ tipo, onFine }: Props) {
  const { t } = useTranslation();
  const passi = PASSI[tipo];
  const [i, setI] = useState(0);
  const punto = passi[i]!;
  const avanti = useRef<HTMLButtonElement>(null);
  const fine = () => {
    segnaVisto(tipo);
    onFine();
  };
  useEffect(() => {
    avanti.current?.focus();
  }, [i]);
  useEffect(() => {
    const tasto = (e: KeyboardEvent) => {
      if (e.key === "Escape") fine();
    };
    window.addEventListener("keydown", tasto);
    return () => window.removeEventListener("keydown", tasto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const chiave = punto === "video" ? `tutorial.video_${tipo === "gruppo" ? "gruppo" : "1v1"}` : `tutorial.${punto === "esci" ? "esci_suoni" : punto}`;
  return createPortal(
    <div className="ld-tutorial" role="dialog" aria-modal="true" aria-labelledby="ld-tut-titolo" data-tipo={tipo} data-passo={punto}>
      <div className="ld-tutorial-carta">
        <div className="ld-tutorial-testa">
          <h2 id="ld-tut-titolo">{t(`tutorial.titolo_${tipo}`)}</h2>
          <button type="button" className="pulsante-link" onClick={fine}>
            {t("tutorial.salta")}
          </button>
        </div>
        <Disegno tipo={tipo} attivo={punto} />
        <div className="ld-tutorial-spiega" aria-live="polite">
          <strong>{t(`${chiave}_titolo`)}</strong>
          <p>{t(`${chiave}_testo`)}</p>
        </div>
        <div className="ld-tutorial-puntini" aria-hidden="true">
          {passi.map((p, k) => (
            <span key={p} data-attivo={k === i} />
          ))}
        </div>
        <div className="ld-tutorial-pulsanti">
          {i > 0 && (
            <button type="button" className="pulsante" onClick={() => setI(i - 1)}>
              {t("tutorial.indietro")}
            </button>
          )}
          <button ref={avanti} type="button" className="ld-cta ld-cta-centrata" onClick={() => {
              vibra("scelta");
              if (i < passi.length - 1) setI(i + 1);
              else fine();
            }}>
            {i < passi.length - 1 ? t("tutorial.avanti", { n: i + 1, totale: passi.length }) : t("tutorial.fine")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
